/*
 * Simple Fencing Post Configurator
 * Version: v1.1.0
 * Date: 2026-06-02
 *
 * v1.0.5:
 * - Keeps all v1.0.4 behaviour.
 * - Caches option fieldsets for faster updates.
 * - Reduces repeated DOM queries during selection and summary updates.
 *
 * Note:
 * - Thumbnail rendering remains owned by product-variant-options.liquid.
 * - This script should not inject thumbnails.
 */

(function () {
  'use strict';

  const SCRIPT_VERSION = 'v1.1.0';

  const MASTER_HANDLES = new Set([
    'posts-for-picket-fence-panels',
    'posts-for-pool-fence-panels',
    'posts-for-ranch-fencing',
    'posts-for-cascade-privacy-fencing',
  ]);

  const DEFAULT_SELECTIONS_BY_HANDLE = {
    'posts-for-picket-fence-panels': [
      'Straight Picket',
      'End Post',
      '4"x4" 60" (152cm) Post / 3ft (91cm) Panel',
    ],
  };

  const MONEY_FORMATTER = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  });

  const POST_TYPE_ORDER = {
    'panel only': 1, 'no post': 1,
    'end post': 2, 'end': 2,
    'line post': 3, 'line': 3,
    'corner post': 4, 'corner': 4,
    't-post': 5, 't post': 5, 't': 5,
    'cross post': 6, 'cross-post': 6, 'x-post': 6, 'x post': 6, 'cross': 6, '4-way': 6, '4 way': 6, 'four way': 6,
    'blank post': 7, 'blank': 7,
  };

  const POST_TYPE_DESCRIPTIONS = {
    'Panel Only': 'No post is included. Use this only where a separate post is not required.',
    'End Post': 'Used to start or finish a fence run. Routed on one side for one panel connection.',
    'Line Post': 'Used between two panels in a straight run. Routed on opposite sides.',
    'Corner Post': 'Used for a 90-degree turn. Routed on adjacent sides.',
    'T-Post': 'Used for a three-way junction where three fence panels meet.',
    'T Post': 'Used for a three-way junction where three fence panels meet.',
    'Cross Post': 'Used for a four-way junction where four fence panels meet at one post.',
    'Blank Post': 'No routed holes. Used for custom layouts, gate latch points or special installations.',
  };

  const POST_CAPS_BY_SIZE = {
    '4x4': [
      { key: 'NO_CAP', label: 'No Post Cap', price: 0, variantId: null, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-none.png?v=1779710493' },
      { key: 'FLAT_EXTERNAL', label: 'Flat External', price: 4.95, variantId: 56783800009084, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-external-flat.png?v=1779710493' },
      { key: 'FLAT_INTERNAL', label: 'Flat Internal', price: 3.95, variantId: 56783800041852, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-internal-flat.png?v=1779710493' },
      { key: 'GOTHIC', label: 'Gothic', price: 7.95, variantId: 56783800074620, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-gothic.png?v=1779710494' },
      { key: 'NEW_ENGLAND', label: 'New England', price: 7.95, variantId: 56783800107388, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-new-england.png?v=1779710493' },
    ],
    '5x5': [
      { key: 'NO_CAP', label: 'No Post Cap', price: 0, variantId: null, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-none.png?v=1779710493' },
      { key: 'FLAT_EXTERNAL', label: 'Flat External', price: 5.95, variantId: 56783800140156, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-external-flat.png?v=1779710493' },
      { key: 'FLAT_INTERNAL', label: 'Flat Internal', price: 4.95, variantId: 56783800172924, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-internal-flat.png?v=1779710493' },
      { key: 'GOTHIC', label: 'Gothic', price: 8.95, variantId: 56783800205692, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-gothic.png?v=1779710494' },
      { key: 'NEW_ENGLAND', label: 'New England', price: 8.95, variantId: 56783800238460, imageUrl: 'https://cdn.shopify.com/s/files/1/0182/2146/5664/files/post-cap-new-england.png?v=1779710493' },
    ],
  };

  let isAutoSelectingVariant = false;

  function normalise(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/\s+/g, ' ');
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      }[char];
    });
  }

  function money(value) {
    const numeric = Number(value || 0);
    return MONEY_FORMATTER.format(Number.isFinite(numeric) ? numeric : 0);
  }

  function getHandle() {
    const root = document.querySelector('[data-sf-post-configurator-root]');
    if (root && root.dataset.productHandle) return root.dataset.productHandle;

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const productsIndex = pathParts.indexOf('products');

    return productsIndex >= 0 ? pathParts[productsIndex + 1] || '' : '';
  }

  function findVariantRadios() {
    return document.querySelector('variant-radios');
  }

  function readVariants(variantRadios) {
    const script = variantRadios && variantRadios.querySelector('script[type="application/json"]');

    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[SF Post Configurator] Could not parse product variants JSON.', error);
      return [];
    }
  }

  function labelText(label) {
    const clone = label.cloneNode(true);
    clone.querySelectorAll('img, svg, .visually-hidden').forEach((node) => node.remove());
    return clone.textContent.trim();
  }

  function optionNameFromFieldset(fieldset) {
    const legend = fieldset.querySelector('legend');
    return legend ? legend.textContent.trim() : '';
  }

  function isPostTypeFieldset(fieldset) {
    const name = normalise(optionNameFromFieldset(fieldset));
    return name.includes('post type') || name.includes('fence post');
  }

  function isSizeFieldset(fieldset) {
    const name = normalise(optionNameFromFieldset(fieldset));
    return name.includes('post size') || name.includes('panel height') || name.includes('post & panel');
  }

  function isMainStyleFieldset(fieldset, index) {
    if (index !== 0) return false;
    if (isPostTypeFieldset(fieldset) || isSizeFieldset(fieldset)) return false;
    return true;
  }

  function buildState(handle, variantRadios, variants) {
    const fieldsets = Array.from(variantRadios.querySelectorAll('fieldset'));

    const state = {
      handle,
      variantRadios,
      variants,
      fieldsets,
      styleFieldset: null,
      postTypeFieldset: null,
      sizeFieldset: null,
      selectedCapKey: 'NO_CAP',
    };

    fieldsets.forEach((fieldset, index) => {
      if (!state.styleFieldset && isMainStyleFieldset(fieldset, index)) {
        state.styleFieldset = fieldset;
      }

      if (!state.postTypeFieldset && isPostTypeFieldset(fieldset)) {
        state.postTypeFieldset = fieldset;
      }

      if (!state.sizeFieldset && isSizeFieldset(fieldset)) {
        state.sizeFieldset = fieldset;
      }
    });

    return state;
  }

  function variantOptions(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options;

    return [variant.option1, variant.option2, variant.option3].filter((value) => {
      return value !== undefined && value !== null;
    });
  }

  function selectedValueFromFieldset(fieldset) {
    if (!fieldset) return '';

    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;

    const select = fieldset.querySelector('select');
    return select ? select.value : '';
  }

  function getSelectedOptions(state) {
    return state.fieldsets.map(selectedValueFromFieldset);
  }

  function findSelectedVariant(variants, selectedOptions) {
    return variants.find((variant) => {
      const options = variantOptions(variant);

      return selectedOptions.every((value, index) => {
        return String(options[index] || '') === String(value || '');
      });
    }) || null;
  }

  function findDefaultVariant(variants, selectedOptions) {
    const available = variants.filter((variant) => variant && variant.available !== false);
    const candidates = available.length ? available : variants;
    const weights = [100, 10, 1];

    let bestVariant = null;
    let bestScore = -1;

    candidates.forEach((variant) => {
      const options = variantOptions(variant);

      const score = selectedOptions.reduce((total, selected, index) => {
        if (!selected) return total;

        return String(options[index] || '') === String(selected)
          ? total + (weights[index] || 1)
          : total;
      }, 0);

      if (score > bestScore) {
        bestScore = score;
        bestVariant = variant;
      }
    });

    return bestVariant;
  }

  function hasExplicitVariantInUrl() {
    try {
      return new URLSearchParams(window.location.search).has('variant');
    } catch (error) {
      return false;
    }
  }

  function findPreferredDefaultVariant(handle, variants) {
    const preferredOptions = DEFAULT_SELECTIONS_BY_HANDLE[handle];

    if (!preferredOptions) return null;

    const available = variants.filter((variant) => variant && variant.available !== false);
    const candidates = available.length ? available : variants;

    return candidates.find((variant) => {
      const options = variantOptions(variant);

      return preferredOptions.every((preferredValue, index) => {
        return normalise(options[index]) === normalise(preferredValue);
      });
    }) || null;
  }

  function selectVariantOptions(state, variant) {
    const options = variantOptions(variant);
    let changedInput = null;

    state.fieldsets.forEach((fieldset, index) => {
      const targetValue = options[index];

      if (targetValue === undefined || targetValue === null) return;

      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((radio) => {
        return String(radio.value || '') === String(targetValue || '');
      });

      if (input && !input.checked) {
        input.checked = true;
        changedInput = input;
      }
    });

    if (changedInput) {
      changedInput.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  function applyPreferredInitialSelection(state) {
    if (hasExplicitVariantInUrl()) return false;

    const preferredVariant = findPreferredDefaultVariant(state.handle, state.variants);
    if (!preferredVariant) return false;

    const selectedVariant = findSelectedVariant(state.variants, getSelectedOptions(state));

    if (selectedVariant && String(selectedVariant.id) === String(preferredVariant.id)) {
      return false;
    }

    isAutoSelectingVariant = true;
    const changed = selectVariantOptions(state, preferredVariant);
    isAutoSelectingVariant = false;

    return changed;
  }

  function ensureValidVariantSelection(state) {
    const selectedOptions = getSelectedOptions(state);
    const selectedVariant = findSelectedVariant(state.variants, selectedOptions);

    if (selectedVariant || isAutoSelectingVariant) return selectedVariant;

    const defaultVariant = findDefaultVariant(state.variants, selectedOptions);
    if (!defaultVariant) return null;

    isAutoSelectingVariant = true;
    selectVariantOptions(state, defaultVariant);
    isAutoSelectingVariant = false;

    return findSelectedVariant(state.variants, getSelectedOptions(state)) || defaultVariant;
  }

  function enhanceMainStyleOptions(state) {
    if (!state.styleFieldset) return;

    state.styleFieldset.classList.add('sf-post-configurator-native', 'sf-post-configurator-native--style');

    Array.from(state.styleFieldset.querySelectorAll('label')).forEach((label) => {
      label.classList.add('sf-post-configurator__style-card');
    });
  }

  function enhancePostTypeOptions(state) {
    if (!state.postTypeFieldset) return;

    state.postTypeFieldset.classList.add('sf-post-configurator-native', 'sf-post-configurator-native--posts');

    Array.from(state.postTypeFieldset.querySelectorAll('label')).forEach((label) => {
      const raw = label.getAttribute('title') || labelText(label);
      const key = normalise(raw);

      label.classList.add('sf-post-configurator__post-type-card');
      label.style.order = String(POST_TYPE_ORDER[key] || 99);
    });
  }

  function createPanel(root) {
    let panel = document.querySelector('[data-sf-post-configurator-panel]');
    if (panel) return panel;

    panel = document.createElement('div');
    panel.className = 'sf-post-configurator';
    panel.setAttribute('data-sf-post-configurator-panel', '');

    panel.innerHTML = `
      <div class="sf-post-configurator__card">
        <div class="sf-post-configurator__card-title">Current selection</div>
        <div class="sf-post-configurator__description" data-sf-post-configurator-description></div>
        <div class="sf-post-configurator__summary" data-sf-post-configurator-summary></div>

        <div class="sf-post-configurator__cap-selector" data-sf-post-cap-selector>
          <div class="sf-post-configurator__cap-title">Optional post cap</div>
          <div class="sf-post-configurator__cap-options" data-sf-post-cap-options></div>
        </div>
      </div>
    `;

    const mount = document.querySelector('[data-sf-post-configurator-root]');

    if (mount) {
      mount.appendChild(panel);
    } else if (root && root.parentNode) {
      root.parentNode.insertBefore(panel, root.nextSibling);
    }

    return panel;
  }

  function findSelectedMainStyle(state) {
    return selectedValueFromFieldset(state.styleFieldset);
  }

  function findSelectedPostType(state) {
    return selectedValueFromFieldset(state.postTypeFieldset);
  }

  function findSelectedSize(state) {
    return selectedValueFromFieldset(state.sizeFieldset);
  }

  function parsePostAndPanelSize(value) {
    const raw = String(value || '').trim();

    if (!raw) {
      return {
        post: '',
        panel: '',
      };
    }

    const parts = raw.split('/');

    return {
      post: parts[0] ? parts[0].trim() : raw,
      panel: parts[1] ? parts.slice(1).join('/').trim() : '',
    };
  }

  function detectPostCapSize(postText) {
    const value = normalise(postText);

    if (value.includes('5"x5') || value.includes('5x5')) return '5x5';

    return '4x4';
  }

  function getCapOptionsForCurrentPost(state) {
    const size = findSelectedSize(state);
    const parsedSize = parsePostAndPanelSize(size);
    const capSize = detectPostCapSize(parsedSize.post);

    return POST_CAPS_BY_SIZE[capSize] || POST_CAPS_BY_SIZE['4x4'];
  }

  function getSelectedCap(state) {
    const options = getCapOptionsForCurrentPost(state);

    return options.find((cap) => cap.key === state.selectedCapKey) || options[0];
  }

  function updateNativeOptionTitles(state) {
    state.fieldsets.forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!checked) return;

      const legend = fieldset.querySelector('legend');
      const subtitle = fieldset.querySelector('.subtitle');

      if (!subtitle) return;

      let groupName = legend ? legend.textContent.trim() : '';

      if (groupName.includes(':')) {
        groupName = groupName.split(':')[0].trim();
      }

      if (groupName) {
        subtitle.textContent = `${groupName}: ${checked.value}`;
      }
    });
  }

  function renderCapSelector(panel, state) {
    const target = panel.querySelector('[data-sf-post-cap-options]');
    if (!target) return;

    const capOptions = getCapOptionsForCurrentPost(state);

    if (!capOptions.some((cap) => cap.key === state.selectedCapKey)) {
      state.selectedCapKey = 'NO_CAP';
    }

    target.innerHTML = capOptions.map((cap) => {
      const checked = cap.key === state.selectedCapKey ? 'checked' : '';
      const priceText = cap.price > 0 ? `+${money(cap.price)}` : '+£0.00';
      const id = `sf-post-cap-${cap.key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

      return `
        <input
          type="radio"
          id="${id}"
          name="sf-post-cap"
          value="${escapeHtml(cap.key)}"
          ${checked}
        >
        <label for="${id}" class="sf-post-configurator__cap-card" title="${escapeHtml(cap.label)}">
          ${cap.imageUrl ? `
            <img
              src="${escapeHtml(cap.imageUrl)}"
              alt="${escapeHtml(cap.label)}"
              width="60"
              height="60"
              loading="lazy"
              decoding="async"
            >
          ` : ''}
          <span class="sf-post-configurator__cap-text">${escapeHtml(cap.label)}</span>
          <small>${priceText}</small>
        </label>
      `;
    }).join('');
  }

  function updatePanel(panel, state) {
    updateNativeOptionTitles(state);
    renderCapSelector(panel, state);
    
    const variant = ensureValidVariantSelection(state);
    const style = findSelectedMainStyle(state);
    const postType = findSelectedPostType(state);
    const size = findSelectedSize(state);
    const desc = POST_TYPE_DESCRIPTIONS[postType] || 'Select the post style and compatible panel height for your fencing layout.';

    const descEl = panel.querySelector('[data-sf-post-configurator-description]');
    const summaryEl = panel.querySelector('[data-sf-post-configurator-summary]');

    if (descEl) {
      descEl.innerHTML = `<strong>${escapeHtml(postType || 'Selected post')}:</strong> ${escapeHtml(desc)}`;
    }

    if (!summaryEl || !variant) return;

    const sku = escapeHtml(variant.sku || 'No SKU');
    const postPriceNumber = variant.price ? Number(variant.price) / 100 : Number(variant.price || 0);
    const selectedCap = getSelectedCap(state);
    const capPriceNumber = selectedCap ? Number(selectedCap.price || 0) : 0;
    const totalPrice = money(postPriceNumber + capPriceNumber);
    const price = money(postPriceNumber);

    const parsedSize = parsePostAndPanelSize(size);

    summaryEl.innerHTML = `
      <div class="sf-post-configurator__selection-grid">
        ${style ? `
          <div class="sf-post-configurator__selection-row">
            <span class="sf-post-configurator__selection-label">Fence Style</span>
            <span class="sf-post-configurator__selection-value">${escapeHtml(style)}</span>
          </div>
        ` : ''}

        ${postType ? `
          <div class="sf-post-configurator__selection-row">
            <span class="sf-post-configurator__selection-label">Post Type</span>
            <span class="sf-post-configurator__selection-value">${escapeHtml(postType)}</span>
          </div>
        ` : ''}

        ${parsedSize.post ? `
          <div class="sf-post-configurator__selection-row">
            <span class="sf-post-configurator__selection-label">Post</span>
            <span class="sf-post-configurator__selection-value">${escapeHtml(parsedSize.post)}</span>
          </div>
        ` : ''}

        ${parsedSize.panel ? `
          <div class="sf-post-configurator__selection-row">
            <span class="sf-post-configurator__selection-label">Compatible Panel</span>
            <span class="sf-post-configurator__selection-value">${escapeHtml(parsedSize.panel)}</span>
          </div>
        ` : ''}

        ${selectedCap ? `
          <div class="sf-post-configurator__selection-row">
            <span class="sf-post-configurator__selection-label">Post Cap</span>
            <span class="sf-post-configurator__selection-value">${escapeHtml(selectedCap.label)}</span>
          </div>
        ` : ''}
      </div>

      <div class="sf-post-configurator__price-grid">
        <span class="sf-post-configurator__price-label">SKU:</span>
        <strong>${sku}</strong>

        <span class="sf-post-configurator__price-label">Post:</span>
        <span>${price}</span>

        <span class="sf-post-configurator__price-label">Cap:</span>
        <strong>${escapeHtml(selectedCap.label)}</strong>

        <span class="sf-post-configurator__price-label">Total:</span>
        <strong>${totalPrice}</strong>
      </div>
    `;
  }

  function bindUpdates(panel, state) {
    let updateQueued = false;

    const update = () => {
      if (isAutoSelectingVariant || updateQueued) return;

      updateQueued = true;

      queueMicrotask(() => {
        updateQueued = false;
        updatePanel(panel, state);
      });
    };

    panel.addEventListener('change', (event) => {
      if (!event.target.matches('input[name="sf-post-cap"]')) return;

      state.selectedCapKey = event.target.value || 'NO_CAP';
      updatePanel(panel, state);
    });

    state.variantRadios.addEventListener('change', update);
    document.addEventListener('variant:change', update);

    updatePanel(panel, state);
  }

  function init() {
    const handle = getHandle();

    if (!MASTER_HANDLES.has(handle)) return;

    const variantRadios = findVariantRadios();
    if (!variantRadios) return;

    const variants = readVariants(variantRadios);
    if (!variants.length) return;

    const state = buildState(handle, variantRadios, variants);

    document.documentElement.classList.add('sf-post-configurator-loaded');
    document.body.classList.add('sf-post-configurator-page');

    enhanceMainStyleOptions(state);
    enhancePostTypeOptions(state);
    applyPreferredInitialSelection(state);

    const panel = createPanel(variantRadios);

    bindUpdates(panel, state);

    const root = document.querySelector('[data-sf-post-configurator-root]');

    if (root) {
      root.sfPostConfigurator = {
        buildCartPayload(formData) {
          const selectedCap = getSelectedCap(state);

          if (!selectedCap || selectedCap.key === 'NO_CAP' || !selectedCap.variantId) {
            return null;
          }

          const quantity = Number(formData.get('quantity') || 1);

          return {
            mainProperties: {
              'Optional Post Cap': selectedCap.label,
            },
            items: [
              {
                id: Number(selectedCap.variantId),
                quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
                properties: {
                  'Added with': 'Post configurator',
                  'For post SKU': findSelectedVariant(state.variants, getSelectedOptions(state))?.sku || '',
                },
              },
            ],
          };
        }
      };
    }

    console.info(`[SF Post Configurator] ${SCRIPT_VERSION} active for ${handle}`);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();